import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowUp,
  Plus,
  Folder,
  Globe,
  ChevronDown,
  Mic,
  Check,
  Users,
  Lock,
  Paperclip,
  Loader2,
  X,
} from 'lucide-react';
import { AIModel } from '../types/models';
import { MessageRequest } from '../schemas/chat';
import { fileAPI } from '../services/api/fileApi';
import { researchAPI } from '../services/api/researchApi';
import { UploadedFile, LiteratureSource, LiteratureOptions } from '../schemas/chat';
import { useAuth } from '../contexts/AuthContext';
import type { KnowledgeBaseShare } from '../services/knowledgeBaseService';

interface EnhancedChatInputProps {
  onSendMessage?: (data: MessageRequest) => void;
  placeholder?: string;
  disabled?: boolean;
  initialValue?: string;
  // Model state and methods from useConversation
  selectedModel?: string | null;
  models: AIModel[];
  onSelectModel?: (modelId: string) => void;
  // Knowledge base state and methods from useConversation
  kbState: {
    knowledgeBases: Array<{
      id: string;
      name: string;
      description?: string | null;
      is_public: boolean;
      is_active: boolean;
      owner_id: number;
      document_count?: number;
      shares?: KnowledgeBaseShare[];
    }>;
    selectedKnowledgeBases: Set<string>;
    loading: boolean;
    error: string | null;
  };
  onToggleKnowledgeBase?: (kbId: string) => void;
  onSelectAllKnowledgeBases?: () => void;
  onClearAllKnowledgeBases?: () => void;
  // Literature state and methods from useConversation
  literatureSources: string[];
  onSelectLiterature?: (sources: string[]) => void;
  literatureOptions: LiteratureOptions | null;
  onSelectLiteratureOptions?: (options: LiteratureOptions) => void;
  // Optional: Hide knowledge base selector and auto-include current KB
  hideKnowledgeBaseSelector?: boolean;
  currentKnowledgeBaseId?: string;
  // Files page selected files
  selectedFileIds?: string[];
  selectedFiles?: Array<{
    id: string;
    name: string;
  }>;
  onRemoveSelectedFile?: (fileId: string) => void;
}

interface UploadedFileLocal {
  id: string;
  file: File;
  name: string;
  size: number;
  isUploading: boolean;
  isDeleting?: boolean; // Track if file is being deleted
  progress?: number;
  fileMetadata?: UploadedFile; // Store the full file metadata from the server
  error?: string;
}

const EnhancedChatInput: React.FC<EnhancedChatInputProps> = ({
  onSendMessage,
  placeholder,
  disabled = false,
  initialValue,
  selectedModel,
  models,
  onSelectModel,
  kbState,
  onToggleKnowledgeBase,
  onSelectAllKnowledgeBases,
  onClearAllKnowledgeBases,
  literatureSources: selectedLiteratureSources,
  onSelectLiterature,
  literatureOptions,
  onSelectLiteratureOptions,
  hideKnowledgeBaseSelector = false,
  currentKnowledgeBaseId,
  selectedFileIds,
  selectedFiles,
  onRemoveSelectedFile,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const currentUserId = user?.id;
  const [inputValue, setInputValue] = useState(initialValue || '');
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [showDatabaseDropdown, setShowDatabaseDropdown] = useState(false);
  const [showExternalDropdown, setShowExternalDropdown] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileLocal[]>([]);
  const [literatureSourcesList, setLiteratureSourcesList] = useState<LiteratureSource[]>([]);
  const [literatureSourcesLoading, setLiteratureSourcesLoading] = useState(false);
  const [literatureSourcesError, setLiteratureSourcesError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Literature options UI state
  const [showLiteratureOptions, setShowLiteratureOptions] = useState(false);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modelDropdownRef = useRef<HTMLDivElement>(null);
  const databaseDropdownRef = useRef<HTMLDivElement>(null);
  const externalDropdownRef = useRef<HTMLDivElement>(null);

  // Update input value when initialValue changes
  useEffect(() => {
    if (initialValue !== undefined) {
      setInputValue(initialValue);
      // Focus the input after setting the value
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [initialValue]);

  // Organize knowledge bases by ownership with safety checks
  const knowledgeBases = Array.isArray(kbState.knowledgeBases) ? kbState.knowledgeBases : [];
  // Filter out inactive knowledge bases and sort by document count descending
  const activeKnowledgeBases = knowledgeBases
    .filter(kb => kb && kb.is_active !== false)
    .sort((a, b) => (b.document_count || 0) - (a.document_count || 0));

  // Public Knowledge Bases: is_public == true (globe KBs)
  const publicKnowledgeBases = activeKnowledgeBases.filter(kb => kb && kb.is_public);

  // My Knowledge Bases: owner_id == currentUserId AND not public
  const myKnowledgeBases = activeKnowledgeBases.filter(
    kb => kb && kb.owner_id === currentUserId && !kb.is_public
  );

  // Shared With Me: shares array includes currentUserId AND not public
  const sharedKnowledgeBases = activeKnowledgeBases.filter(kb => {
    if (!kb || kb.owner_id === currentUserId || kb.is_public) return false;
    // Check if the KB is shared with the current user
    return kb.shares && kb.shares.some(share => share.shared_with_user_id === currentUserId);
  });

  // Helper function to check if files are uploading and submit message
  const attemptSubmitMessage = () => {
    if (!inputValue.trim() || !onSendMessage) return;

    // Prevent submission if files are still uploading
    const hasUploadingFiles = uploadedFiles.some(f => f.isUploading);
    if (hasUploadingFiles) {
      console.warn('Cannot submit while files are uploading');
      return;
    }

    // Get selected knowledge base IDs, or use current KB if hidden
    let knowledgeBaseIds: string[] | undefined;
    if (hideKnowledgeBaseSelector && currentKnowledgeBaseId) {
      knowledgeBaseIds = [currentKnowledgeBaseId];
    } else {
      const selectedIds = Array.from(kbState.selectedKnowledgeBases);
      knowledgeBaseIds = selectedIds.length > 0 ? selectedIds : undefined;
    }
    // Get uploaded file metadata (only completed uploads)
    const completedFiles = uploadedFiles
      .filter(f => !f.isUploading && f.fileMetadata)
      .map(f => f.fileMetadata!);

    onSendMessage({
      content: inputValue,
      model_id: selectedModel || undefined,
      knowledge_base_ids: knowledgeBaseIds,
      files: completedFiles.length > 0 ? completedFiles : undefined,
      literature_sources:
        selectedLiteratureSources.length > 0 ? selectedLiteratureSources : undefined,
      literature_options: selectedLiteratureSources.length > 0 ? literatureOptions : undefined,
    });
    setInputValue('');
    setUploadedFiles([]); // Clear uploaded files after sending
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    attemptSubmitMessage();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      attemptSubmitMessage();
    }
  };

  // Fetch literature sources on component mount
  const fetchLiteratureSources = useCallback(async () => {
    setLiteratureSourcesLoading(true);
    setLiteratureSourcesError(null);
    try {
      const response = await researchAPI.getLiteratureSources();
      setLiteratureSourcesList(response.data.sources);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch literature sources:', error);
      setLiteratureSourcesError(
        t('enhancedChatInput.failedToLoadLiteratureSources', 'Failed to load literature sources')
      );
    } finally {
      setLiteratureSourcesLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchLiteratureSources();
  }, [fetchLiteratureSources]);

  // No need to initialize from props anymore - state comes from useConversation
  // useEffect(() => {
  //   if (initialKnowledgeBaseIds && initialKnowledgeBaseIds.length > 0) {
  //     setSelectedKnowledgeBases(initialKnowledgeBaseIds);
  //   }
  // }, [initialKnowledgeBaseIds, setSelectedKnowledgeBases]);

  // useEffect(() => {
  //   if (initialLiteratureSources && initialLiteratureSources.length > 0) {
  //     setSelectedExternal(new Set(initialLiteratureSources));
  //   }
  // }, [initialLiteratureSources]);

  // useEffect(() => {
  //   if (initialLiteratureOptions) {
  //     setLiteratureOptions(initialLiteratureOptions);
  //   }
  // }, [initialLiteratureOptions]);

  // Close dropdowns when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(event.target as Node)) {
        setShowModelDropdown(false);
      }
      if (
        databaseDropdownRef.current &&
        !databaseDropdownRef.current.contains(event.target as Node)
      ) {
        setShowDatabaseDropdown(false);
      }
      if (
        externalDropdownRef.current &&
        !externalDropdownRef.current.contains(event.target as Node)
      ) {
        setShowExternalDropdown(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowModelDropdown(false);
        setShowDatabaseDropdown(false);
        setShowExternalDropdown(false);
        setShowLiteratureOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleDatabaseToggle = (kbId: string) => {
    if (onToggleKnowledgeBase) {
      onToggleKnowledgeBase(kbId);
    }
  };

  const handleExternalToggle = (sourceId: string) => {
    if (onSelectLiterature) {
      const isSelected = selectedLiteratureSources.includes(sourceId);
      if (isSelected) {
        onSelectLiterature(selectedLiteratureSources.filter(id => id !== sourceId));
      } else {
        onSelectLiterature([...selectedLiteratureSources, sourceId]);
      }
    }
  };

  const handleSelectAll = () => {
    if (onSelectAllKnowledgeBases) {
      onSelectAllKnowledgeBases();
    }
  };

  const handleClearAll = () => {
    if (onClearAllKnowledgeBases) {
      onClearAllKnowledgeBases();
    }
  };

  // File upload handlers
  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newFiles: UploadedFileLocal[] = Array.from(files).map(file => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      name: file.name,
      size: file.size,
      isUploading: true,
      progress: 0,
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);

    // Upload files immediately when selected
    for (const uploadedFile of newFiles) {
      await uploadFile(uploadedFile);
    }

    // Reset file input
    event.target.value = '';
  };

  const uploadFile = async (uploadedFile: UploadedFileLocal) => {
    try {
      // Call the file upload API
      const response = await fileAPI.uploadFile(uploadedFile.file, progress => {
        // Update upload progress
        setUploadedFiles(prev =>
          prev.map(file => (file.id === uploadedFile.id ? { ...file, progress } : file))
        );
      });

      // Store the full file metadata from the response
      const fileMetadata = response.data.file;

      // Update the file with the server metadata
      setUploadedFiles(prev =>
        prev.map(file =>
          file.id === uploadedFile.id
            ? {
                ...file,
                isUploading: false,
                progress: 100,
                fileMetadata, // Store the full file metadata for later use in message submission
              }
            : file
        )
      );
    } catch (error) {
      console.error('File upload failed:', error);
      // Mark the file as failed
      setUploadedFiles(prev =>
        prev.map(file =>
          file.id === uploadedFile.id
            ? {
                ...file,
                isUploading: false,
                error: error instanceof Error ? error.message : 'Upload failed',
              }
            : file
        )
      );
    }
  };

  const handleRemoveFile = async (localFileId: string) => {
    // Find the file in the uploaded files list
    const fileToRemove = uploadedFiles.find(f => f.id === localFileId);

    // If file was successfully uploaded, delete it from the server
    if (fileToRemove?.fileMetadata) {
      // Set deleting state
      setUploadedFiles(prev =>
        prev.map(file => (file.id === localFileId ? { ...file, isDeleting: true } : file))
      );

      try {
        await fileAPI.deleteFile(fileToRemove.fileMetadata.id);
        console.log(`Deleted file from server: ${fileToRemove.fileMetadata.id}`);
      } catch (error) {
        console.error('Failed to delete file from server:', error);
        // Remove deleting state on error
        setUploadedFiles(prev =>
          prev.map(file => (file.id === localFileId ? { ...file, isDeleting: false } : file))
        );
        return; // Don't remove from local state if deletion failed
      }
    }

    // Remove from local state
    setUploadedFiles(prev => prev.filter(file => file.id !== localFileId));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    // Validate file types
    const validFiles: File[] = [];
    const acceptPattern = /^image\/.*|\.pdf|\.doc|\.docx|\.txt$/i;

    for (const file of Array.from(files)) {
      if (acceptPattern.test(file.name) || file.type.startsWith('image/')) {
        validFiles.push(file);
      }
    }

    if (validFiles.length === 0) return;

    const newFiles: UploadedFileLocal[] = validFiles.map(file => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      name: file.name,
      size: file.size,
      isUploading: true,
      progress: 0,
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);

    // Upload files immediately
    for (const uploadedFile of newFiles) {
      await uploadFile(uploadedFile);
    }
  };

  // Get selected model display name
  const getSelectedModelDisplayName = () => {
    const selectedModelObj = models.find(model => model.id === selectedModel);
    return selectedModelObj?.display_name || '选择模型';
  };

  const badges = [
    {
      id: 'model',
      icon: null,
      text: getSelectedModelDisplayName(),
      dropdown: showModelDropdown,
      setDropdown: setShowModelDropdown,
      ref: modelDropdownRef,
      options: [] as string[], // We'll handle models separately
      testId: undefined as string | undefined,
    },
    ...(hideKnowledgeBaseSelector
      ? []
      : [
          {
            id: 'database' as const,
            icon: Folder,
            text:
              kbState.selectedKnowledgeBases.size > 0
                ? (() => {
                    const selectedKbs = activeKnowledgeBases.filter(
                      kb => kb && kb.id && kbState.selectedKnowledgeBases.has(kb.id)
                    );
                    const kbArray = selectedKbs.map(kb => kb.name || 'Unknown KB');
                    // Helper to truncate name to fit within available space
                    // For 3+ items: each name max 6 chars to ensure "+N" is visible
                    // Format: "xxxxxx... | xxxxxx... +N" (approx 28 chars max)
                    const truncateForDisplay = (name: string, maxLength: number = 15) => {
                      return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;
                    };
                    // Match image format: "晓路的资料库" with "蛋白研究组 | +3"
                    if (kbArray.length === 1) {
                      return truncateForDisplay(kbArray[0], 30);
                    } else if (kbArray.length === 2) {
                      const first = truncateForDisplay(kbArray[0], 12);
                      const second = truncateForDisplay(kbArray[1], 12);
                      return `${first} | ${second}`;
                    } else if (kbArray.length > 2) {
                      const first = truncateForDisplay(kbArray[0], 6);
                      const second = truncateForDisplay(kbArray[1], 6);
                      return `${first} | ${second} +${kbArray.length - 2}`;
                    } else {
                      return t('enhancedChatInput.selectDatabase', '选择资料库');
                    }
                  })()
                : t('enhancedChatInput.selectDatabase', '选择资料库'),
            dropdown: showDatabaseDropdown,
            setDropdown: setShowDatabaseDropdown,
            ref: databaseDropdownRef,
            testId: undefined as string | undefined,
          },
        ]),
    {
      id: 'external',
      icon: Globe,
      text:
        selectedLiteratureSources.length > 0
          ? (() => {
              const selectedSources = literatureSourcesList.filter(s =>
                selectedLiteratureSources.includes(s.id)
              );
              const sourceNames = selectedSources.map(s => s.name);
              // Helper to truncate name to fit within available space
              // For 3+ items: each name max 6 chars to ensure "+N" is visible
              // Format: "xxxxxx... | xxxxxx... +N" (approx 28 chars max)
              const truncateForDisplay = (name: string, maxLength: number = 15) => {
                return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;
              };
              // Match image format: "万方数据 | 知网 +1"
              if (sourceNames.length === 1) {
                return truncateForDisplay(sourceNames[0], 30);
              } else if (sourceNames.length === 2) {
                const first = truncateForDisplay(sourceNames[0], 12);
                const second = truncateForDisplay(sourceNames[1], 12);
                return `${first} | ${second}`;
              } else if (sourceNames.length > 2) {
                const first = truncateForDisplay(sourceNames[0], 6);
                const second = truncateForDisplay(sourceNames[1], 6);
                return `${first} | ${second} +${sourceNames.length - 2}`;
              } else {
                return t('enhancedChatInput.selectLiteratureDB', '选择文献库');
              }
            })()
          : t('enhancedChatInput.selectLiteratureDB', '选择文献库'),
      dropdown: showExternalDropdown,
      setDropdown: setShowExternalDropdown,
      ref: externalDropdownRef,
      testId: 'literature-selector-button' as string | undefined,
    },
  ];

  return (
    <div
      className='relative w-full bg-white border border-[#E5E5F2] rounded-[16px] sm:rounded-[24px] shadow-[0_-4px_20px_0_rgba(112,144,176,0.15)] p-3 sm:p-5'
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag Overlay */}
      {isDragging && (
        <div className='absolute inset-0 z-50 flex items-center justify-center bg-[#F3F8FF] border-2 border-dashed border-[#1551DD] rounded-[16px] sm:rounded-[24px] pointer-events-none'>
          <div className='text-center'>
            <Paperclip size={48} className='text-[#1551DD] mx-auto mb-2' />
            <p className='text-[#1551DD] font-semibold text-lg'>
              {t('chat.dropFilesHere', 'Drop files here to upload')}
            </p>
            <p className='text-gray-500 text-sm mt-1'>
              {t('chat.supportedFormats', 'Images, PDF, DOC, DOCX, TXT')}
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className='flex flex-col'>
        {/* Files Display - Includes both uploaded files and selected files from Files page */}
        {(uploadedFiles.length > 0 || (selectedFileIds && selectedFileIds.length > 0)) && (
          <div className='flex flex-wrap gap-2 mb-3'>
            {/* Selected files from Files page */}
            {selectedFileIds &&
              selectedFileIds.length > 0 &&
              selectedFiles?.map(file => (
                <div
                  key={`selected-${file.id}`}
                  className='flex items-center gap-2 px-3 py-2 bg-[#F3F8FF] rounded-[12px] text-sm border border-[#1551DD]'
                >
                  <Paperclip size={16} className='text-[#1551DD]' />
                  <span className='text-gray-700'>{file.name}</span>
                  <button
                    type='button'
                    onClick={() => onRemoveSelectedFile?.(file.id)}
                    className='ml-1 hover:bg-gray-200 rounded-full p-0.5 transition-colors'
                    title='Remove file'
                  >
                    <X size={14} className='text-gray-500' />
                  </button>
                </div>
              ))}
            {/* Uploaded files via plus button */}
            {uploadedFiles.map(file => (
              <div
                key={file.id}
                className='flex items-center gap-2 px-3 py-2 bg-[#F7F7F7] rounded-[12px] text-sm'
              >
                {file.isUploading || file.isDeleting ? (
                  <Loader2 size={16} className='text-gray-600 animate-spin' />
                ) : (
                  <Paperclip size={16} className='text-gray-600' />
                )}
                <span className='text-gray-700'>{file.name}</span>
                <span className='text-gray-500 text-xs'>{formatFileSize(file.size)}</span>
                {!file.isUploading && !file.isDeleting && (
                  <button
                    type='button'
                    onClick={() => handleRemoveFile(file.id)}
                    className='ml-1 hover:bg-gray-200 rounded-full p-0.5 transition-colors'
                    title='Remove file'
                  >
                    <X size={14} className='text-gray-500' />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type='file'
          multiple
          accept='image/*,.pdf,.doc,.docx,.txt'
          onChange={handleFileChange}
          className='hidden'
        />

        {/* Main Input Area */}
        <div className='relative w-full min-h-[100px] sm:min-h-[120px] bg-white rounded-[12px]'>
          {/* Placeholder Text - Top Left with padding */}
          {!inputValue && (
            <div className='absolute top-3 sm:top-4 left-3 sm:left-4 text-[#9CA3AF] text-[14px] sm:text-[16px] font-normal leading-[20px] sm:leading-[22px] pointer-events-none z-10'>
              {placeholder || t('chat.inputPlaceholder', 'Type your message here...')}
            </div>
          )}

          {/* Input Field - Positioned to allow typing with proper padding */}
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            data-testid='chat-input'
            className='w-full min-h-[100px] sm:min-h-[120px] bg-transparent outline-none text-[14px] sm:text-[16px] font-normal leading-[20px] sm:leading-[22px] text-[#475569] px-3 sm:px-4 pt-3 sm:pt-4 resize-none'
            style={{ paddingBottom: '56px' }}
          />

          {/* Bottom Section with Plus Button and Right Buttons */}
          <div className='absolute bottom-0 left-0 right-0 flex items-center justify-between px-3 sm:px-4 pointer-events-none'>
            {/* Left Button - Plus (responsive size) */}
            <button
              type='button'
              onClick={handleFileButtonClick}
              className='flex items-center justify-center w-[40px] h-[40px] sm:w-[48px] sm:h-[48px] bg-[#F7F7F7] rounded-full hover:bg-gray-200 transition-colors duration-200 flex-shrink-0 pointer-events-auto'
              disabled={disabled}
              title='Upload file or image'
            >
              <Plus size={20} className='text-gray-600' />
            </button>

            {/* Right Buttons - Microphone and Send Arrow */}
            <div className='flex items-center gap-1 sm:gap-2 flex-shrink-0 pointer-events-auto'>
              <button
                type='button'
                className='flex items-center justify-center w-[36px] h-[36px] sm:w-[40px] sm:h-[40px] bg-[#F7F7F7] rounded-full hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
                disabled={true}
              >
                <Mic size={18} className='text-gray-600' />
              </button>
              <button
                type='submit'
                data-testid='send-button'
                className='flex items-center justify-center w-[36px] h-[36px] sm:w-[40px] sm:h-[40px] bg-[#1551DD] rounded-full hover:bg-[#0F3BA8] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
                disabled={disabled || !inputValue.trim() || uploadedFiles.some(f => f.isUploading)}
              >
                <ArrowUp size={18} className='text-white' />
              </button>
            </div>
          </div>
        </div>

        {/* Divider Line */}
        <div className='w-full h-[1px] bg-[#E5E5F2] my-3 sm:my-4' />

        {/* Badges Section */}
        <div className='flex items-center gap-2 sm:gap-3 flex-wrap'>
          {badges.map(badge => {
            const isSelected =
              badge.id === 'model' ||
              (badge.id === 'database' && kbState.selectedKnowledgeBases.size > 0) ||
              (badge.id === 'external' && selectedLiteratureSources.length > 0);

            return (
              <div key={badge.id} className='relative' ref={badge.ref}>
                <button
                  type='button'
                  data-testid={badge.testId}
                  onClick={() => badge.setDropdown(!badge.dropdown)}
                  className={`flex items-center gap-1 sm:gap-2 h-[36px] sm:h-[44px] rounded-[20px] sm:rounded-[30px] whitespace-nowrap transition-colors px-3 sm:px-5 text-xs sm:text-sm ${
                    isSelected
                      ? 'bg-[#F3F8FF] text-[#1551DD] hover:bg-gray-100'
                      : 'bg-white text-[#1551DD] border border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {badge.id === 'model' && (
                    <span className='text-[#1551DD] font-bold text-xs sm:text-sm'>🤖</span>
                  )}
                  {badge.icon && <badge.icon size={14} className='text-[#1551DD]' />}
                  <span
                    className={`text-[12px] sm:text-[14px] font-semibold text-[#1551DD] truncate max-w-[150px] sm:max-w-[200px]`}
                    title={badge.text}
                  >
                    {badge.text.length > 40 ? `${badge.text.slice(0, 40)}...` : badge.text}
                  </span>
                  <ChevronDown size={12} className='text-[#1551DD] flex-shrink-0' />
                </button>

                {/* Dropdown Menu */}
                {badge.dropdown && (
                  <div
                    data-testid={badge.id === 'external' ? 'literature-source-dropdown' : undefined}
                    className='fixed bottom-0 left-0 right-0 bg-white border border-[#E5E5F2] border-b-0 rounded-t-[12px] shadow-lg z-[100] max-h-[60vh] overflow-y-auto sm:absolute sm:bottom-full sm:left-0 sm:right-auto sm:mb-2 sm:bg-white sm:border sm:rounded-[12px] sm:max-h-[500px] sm:min-w-[400px]'
                  >
                    {badge.id === 'database' ? (
                      <div className='p-4'>
                        {/* Header */}
                        <div className='flex items-center justify-between mb-4 pb-3 border-b border-[#E5E5F2]'>
                          <h3 className='text-[16px] font-semibold text-[#1F2937]'>
                            {t('enhancedChatInput.selectDatabase')}
                          </h3>
                          <div className='flex items-center gap-4'>
                            <button
                              type='button'
                              onClick={handleSelectAll}
                              className='text-[14px] text-[#1551DD] hover:text-[#0F3BA8] cursor-pointer'
                            >
                              {t('enhancedChatInput.selectAll')}
                            </button>
                            <button
                              type='button'
                              onClick={handleClearAll}
                              className='text-[14px] text-[#1551DD] hover:text-[#0F3BA8] cursor-pointer'
                            >
                              {t('enhancedChatInput.clearAll')}
                            </button>
                          </div>
                        </div>

                        {kbState.loading ? (
                          <div className='flex items-center justify-center py-8'>
                            <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-[#1551DD]'></div>
                            <span className='ml-2 text-[#6B7280]'>加载中...</span>
                          </div>
                        ) : kbState.error ? (
                          <div className='text-center py-8 text-red-500 text-sm'>
                            {kbState.error}
                          </div>
                        ) : (
                          <>
                            {/* My Knowledge Bases Section */}
                            {myKnowledgeBases.length > 0 && (
                              <div className='mb-4'>
                                <h4 className='text-[14px] font-medium text-[#6B7280] mb-3 flex items-center gap-2'>
                                  <Lock size={14} />
                                  {t('enhancedChatInput.myKnowledgeBases', '我的知识库')}
                                </h4>
                                <div className='flex flex-wrap gap-2'>
                                  {myKnowledgeBases.map(kb => (
                                    <button
                                      key={kb.id}
                                      type='button'
                                      onClick={() => handleDatabaseToggle(kb.id)}
                                      title={kb.description || kb.name}
                                      className={`px-4 py-2 rounded-[20px] text-[14px] font-medium transition-colors flex items-center gap-2 max-w-[280px] sm:max-w-[320px] ${
                                        kbState.selectedKnowledgeBases.has(kb.id)
                                          ? 'bg-[#F3F8FF] text-[#1551DD]'
                                          : 'bg-white text-[#1551DD] hover:bg-gray-100'
                                      }`}
                                    >
                                      <span className='truncate'>
                                        {kb.name.length > 40
                                          ? `${kb.name.slice(0, 40)}...`
                                          : kb.name}
                                      </span>
                                      {kb.document_count !== undefined && (
                                        <span className='text-xs opacity-75 flex-shrink-0'>
                                          ({kb.document_count})
                                        </span>
                                      )}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Shared Knowledge Bases Section */}
                            {sharedKnowledgeBases.length > 0 && (
                              <div className='mb-4'>
                                <h4 className='text-[14px] font-medium text-[#6B7280] mb-3 flex items-center gap-2'>
                                  <Users size={14} />
                                  {t('enhancedChatInput.sharedWithMe', '共享给我的')}
                                </h4>
                                <div className='flex flex-wrap gap-2'>
                                  {sharedKnowledgeBases.map(kb => (
                                    <button
                                      key={kb.id}
                                      type='button'
                                      onClick={() => handleDatabaseToggle(kb.id)}
                                      title={kb.description || kb.name}
                                      className={`px-4 py-2 rounded-[20px] text-[14px] font-medium transition-colors flex items-center gap-2 max-w-[280px] sm:max-w-[320px] ${
                                        kbState.selectedKnowledgeBases.has(kb.id)
                                          ? 'bg-[#F3F8FF] text-[#1551DD]'
                                          : 'bg-white text-[#1551DD] hover:bg-gray-100'
                                      }`}
                                    >
                                      <span className='truncate'>
                                        {kb.name.length > 40
                                          ? `${kb.name.slice(0, 40)}...`
                                          : kb.name}
                                      </span>
                                      {kb.document_count !== undefined && (
                                        <span className='text-xs opacity-75 flex-shrink-0'>
                                          ({kb.document_count})
                                        </span>
                                      )}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Public Knowledge Bases Section */}
                            {publicKnowledgeBases.length > 0 && (
                              <div>
                                <h4 className='text-[14px] font-medium text-[#6B7280] mb-3 flex items-center gap-2'>
                                  <Globe size={14} />
                                  {t('enhancedChatInput.publicKnowledgeBases', '公共知识库')}
                                </h4>
                                <div className='flex flex-wrap gap-2'>
                                  {publicKnowledgeBases.map(kb => (
                                    <button
                                      key={kb.id}
                                      type='button'
                                      onClick={() => handleDatabaseToggle(kb.id)}
                                      title={kb.description || kb.name}
                                      className={`px-4 py-2 rounded-[20px] text-[14px] font-medium transition-colors flex items-center gap-2 max-w-[280px] sm:max-w-[320px] ${
                                        kbState.selectedKnowledgeBases.has(kb.id)
                                          ? 'bg-[#F3F8FF] text-[#1551DD]'
                                          : 'bg-white text-[#1551DD] hover:bg-gray-100'
                                      }`}
                                    >
                                      <Globe size={12} className='opacity-75 flex-shrink-0' />
                                      <span className='truncate'>
                                        {kb.name.length > 40
                                          ? `${kb.name.slice(0, 40)}...`
                                          : kb.name}
                                      </span>
                                      {kb.document_count !== undefined && (
                                        <span className='text-xs opacity-75 flex-shrink-0'>
                                          ({kb.document_count})
                                        </span>
                                      )}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Empty state */}
                            {publicKnowledgeBases.length === 0 &&
                              myKnowledgeBases.length === 0 &&
                              sharedKnowledgeBases.length === 0 && (
                                <div className='text-center py-8 text-[#6B7280] text-sm'>
                                  <Folder size={24} className='mx-auto mb-2 opacity-50' />
                                  暂无知识库
                                </div>
                              )}
                          </>
                        )}
                      </div>
                    ) : badge.id === 'model' ? (
                      <div className='py-2'>
                        {models.map((model: AIModel) => {
                          const isSelected = model.id === selectedModel;

                          return (
                            <button
                              key={model.id}
                              type='button'
                              onClick={() => {
                                if (onSelectModel) {
                                  onSelectModel(model.id);
                                }
                                setShowModelDropdown(false);
                              }}
                              className={`w-full text-left px-4 py-2 text-sm transition-colors flex items-center justify-between ${
                                isSelected
                                  ? 'bg-[#F3F8FF] text-[#1551DD] font-medium'
                                  : 'text-[#1551DD] hover:bg-gray-100'
                              }`}
                            >
                              <span>{model.display_name}</span>
                              {isSelected && <Check size={16} className='text-[#1551DD]' />}
                            </button>
                          );
                        })}
                      </div>
                    ) : badge.id === 'external' ? (
                      <div className='p-4'>
                        {/* Header */}
                        <div className='flex items-center justify-between mb-4 pb-3 border-b border-[#E5E5F2]'>
                          <h3 className='text-[16px] font-semibold text-[#1F2937]'>
                            {t('enhancedChatInput.selectLiteratureDB', '选择文献库')}
                          </h3>
                          <button
                            type='button'
                            data-testid='literature-options-button'
                            onClick={() => setShowLiteratureOptions(!showLiteratureOptions)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                              showLiteratureOptions
                                ? 'bg-[#F3F8FF] text-[#1551DD]'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            title={t('enhancedChatInput.literatureOptions', '文献选项')}
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='16'
                              height='16'
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='currentColor'
                              strokeWidth='2'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            >
                              <circle cx='12' cy='12' r='3'></circle>
                              <path d='M12 1v6m0 6v6m0-6h6m-6 0H6'></path>
                            </svg>
                            <span className='text-xs'>
                              {t('enhancedChatInput.options', '选项')}
                            </span>
                          </button>
                        </div>

                        {/* Literature Options Panel */}
                        {showLiteratureOptions && (
                          <div
                            data-testid='literature-options-panel'
                            className='mb-4 p-4 bg-gray-50 rounded-lg space-y-3'
                          >
                            <div>
                              <label className='block text-sm font-medium text-gray-700 mb-1'>
                                {t('enhancedChatInput.maxResults', '最大结果数')}
                              </label>
                              <div className='flex items-center gap-2'>
                                <button
                                  type='button'
                                  data-testid='max-results-decrease'
                                  onClick={() => {
                                    if (onSelectLiteratureOptions) {
                                      const currentValue = literatureOptions?.max_results || 5;
                                      onSelectLiteratureOptions({
                                        ...(literatureOptions || {}),
                                        max_results: Math.max(1, currentValue - 1),
                                      });
                                    }
                                  }}
                                  className='px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium transition-colors'
                                >
                                  -
                                </button>
                                <input
                                  data-testid='max-results-input'
                                  type='number'
                                  min={1}
                                  max={50}
                                  value={literatureOptions?.max_results || 5}
                                  onChange={e => {
                                    if (onSelectLiteratureOptions) {
                                      onSelectLiteratureOptions({
                                        ...(literatureOptions || {}),
                                        max_results: parseInt(e.target.value) || 5,
                                      });
                                    }
                                  }}
                                  className='flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                                />
                                <button
                                  type='button'
                                  data-testid='max-results-increase'
                                  onClick={() => {
                                    if (onSelectLiteratureOptions) {
                                      const currentValue = literatureOptions?.max_results || 5;
                                      onSelectLiteratureOptions({
                                        ...(literatureOptions || {}),
                                        max_results: Math.min(50, currentValue + 1),
                                      });
                                    }
                                  }}
                                  className='px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium transition-colors'
                                >
                                  +
                                </button>
                              </div>
                            </div>

                            <div className='grid grid-cols-2 gap-3'>
                              <div>
                                <label className='block text-sm font-medium text-gray-700 mb-1'>
                                  {t('enhancedChatInput.yearFrom', '起始年份')}
                                </label>
                                <input
                                  data-testid='year-start-input'
                                  type='number'
                                  min={1900}
                                  max={2100}
                                  placeholder='1900'
                                  value={literatureOptions?.year_from || ''}
                                  onChange={e => {
                                    if (onSelectLiteratureOptions) {
                                      onSelectLiteratureOptions({
                                        ...(literatureOptions || {}),
                                        year_from: e.target.value ? parseInt(e.target.value) : null,
                                      });
                                    }
                                  }}
                                  className='w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                                />
                              </div>
                              <div>
                                <label className='block text-sm font-medium text-gray-700 mb-1'>
                                  {t('enhancedChatInput.yearTo', '结束年份')}
                                </label>
                                <input
                                  data-testid='year-end-input'
                                  type='number'
                                  min={1900}
                                  max={2100}
                                  placeholder='2024'
                                  value={literatureOptions?.year_to || ''}
                                  onChange={e => {
                                    if (onSelectLiteratureOptions) {
                                      onSelectLiteratureOptions({
                                        ...(literatureOptions || {}),
                                        year_to: e.target.value ? parseInt(e.target.value) : null,
                                      });
                                    }
                                  }}
                                  className='w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                                />
                              </div>
                            </div>

                            <div>
                              <label className='block text-sm font-medium text-gray-700 mb-1'>
                                {t('enhancedChatInput.language', '语言')}
                              </label>
                              <select
                                data-testid='language-selector'
                                value={literatureOptions?.language || 'chinese'}
                                onChange={e => {
                                  if (onSelectLiteratureOptions) {
                                    onSelectLiteratureOptions({
                                      ...(literatureOptions || {}),
                                      language: e.target.value,
                                    });
                                  }
                                }}
                                className='w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                              >
                                <option value='chinese'>中文</option>
                                <option value='english'>English</option>
                              </select>
                            </div>
                          </div>
                        )}

                        {literatureSourcesLoading ? (
                          <div className='flex items-center justify-center py-8'>
                            <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-[#1551DD]'></div>
                            <span className='ml-2 text-[#6B7280]'>
                              {t('enhancedChatInput.literatureSourcesLoading', 'Loading...')}
                            </span>
                          </div>
                        ) : literatureSourcesError ? (
                          <div className='text-center py-8 px-4'>
                            <p className='text-red-500 text-sm mb-3'>{literatureSourcesError}</p>
                            <button
                              type='button'
                              onClick={fetchLiteratureSources}
                              className='px-4 py-2 bg-[#1551DD] text-white rounded-lg hover:bg-[#0F3BA8] transition-colors text-sm'
                            >
                              {t('enhancedChatInput.retry', 'Retry')}
                            </button>
                          </div>
                        ) : (
                          <>
                            {/* Free sources section */}
                            {literatureSourcesList.filter(s => s.type === 'free').length > 0 && (
                              <div className='mb-4'>
                                <h4 className='text-[14px] font-medium text-[#6B7280] mb-3'>
                                  {t('enhancedChatInput.freeResources', 'Free Resources')}
                                </h4>
                                <div className='flex flex-wrap gap-2'>
                                  {literatureSourcesList
                                    .filter(s => s.type === 'free')
                                    .map(source => {
                                      // Generate test ID based on source ID or name
                                      const getTestId = (source: LiteratureSource) => {
                                        // Map common source IDs to test IDs
                                        const sourceIdMap: Record<string, string> = {
                                          wanfang: 'source-wanfang',
                                          cnki: 'source-cnki',
                                          pubmed: 'source-pubmed',
                                          elsevier: 'source-elsevier',
                                          springer: 'source-springer',
                                          wiley: 'source-wiley',
                                        };
                                        return (
                                          sourceIdMap[source.id] ||
                                          sourceIdMap[source.name.toLowerCase()] ||
                                          `source-${source.id}`
                                        );
                                      };

                                      return (
                                        <button
                                          key={source.id}
                                          type='button'
                                          data-testid={getTestId(source)}
                                          onClick={() => handleExternalToggle(source.id)}
                                          disabled={!source.is_enabled}
                                          className={`px-4 py-2 rounded-[20px] text-[14px] font-medium transition-colors flex items-center gap-2 ${
                                            selectedLiteratureSources.includes(source.id)
                                              ? 'bg-[#F3F8FF] text-[#1551DD]'
                                              : 'bg-white text-[#1551DD] hover:bg-gray-100'
                                          } ${
                                            !source.is_enabled
                                              ? 'opacity-50 cursor-not-allowed'
                                              : ''
                                          }`}
                                          title={source.description}
                                        >
                                          <span>{source.name}</span>
                                          {source.requires_auth && (
                                            <Lock size={12} className='opacity-75' />
                                          )}
                                        </button>
                                      );
                                    })}
                                </div>
                              </div>
                            )}

                            {/* Authorized sources section */}
                            {literatureSourcesList.filter(s => s.type === 'authorized').length >
                              0 && (
                              <div>
                                <h4 className='text-[14px] font-medium text-[#6B7280] mb-3 flex items-center gap-2'>
                                  <Lock size={14} />
                                  {t(
                                    'enhancedChatInput.requiresAuthorization',
                                    'Requires Authorization'
                                  )}
                                </h4>
                                <div className='flex flex-wrap gap-2'>
                                  {literatureSourcesList
                                    .filter(s => s.type === 'authorized')
                                    .map(source => {
                                      // Generate test ID based on source ID or name
                                      const getTestId = (source: LiteratureSource) => {
                                        // Map common source IDs to test IDs
                                        const sourceIdMap: Record<string, string> = {
                                          wanfang: 'source-wanfang',
                                          cnki: 'source-cnki',
                                          pubmed: 'source-pubmed',
                                          elsevier: 'source-elsevier',
                                          springer: 'source-springer',
                                          wiley: 'source-wiley',
                                        };
                                        return (
                                          sourceIdMap[source.id] ||
                                          sourceIdMap[source.name.toLowerCase()] ||
                                          `source-${source.id}`
                                        );
                                      };

                                      return (
                                        <button
                                          key={source.id}
                                          type='button'
                                          data-testid={getTestId(source)}
                                          onClick={() => handleExternalToggle(source.id)}
                                          disabled={!source.is_enabled}
                                          className={`px-4 py-2 rounded-[20px] text-[14px] font-medium transition-colors flex items-center gap-2 ${
                                            selectedLiteratureSources.includes(source.id)
                                              ? 'bg-[#F3F8FF] text-[#1551DD]'
                                              : 'bg-white text-[#1551DD] hover:bg-gray-100'
                                          } ${
                                            !source.is_enabled
                                              ? 'opacity-50 cursor-not-allowed'
                                              : ''
                                          }`}
                                          title={source.description}
                                        >
                                          <span>{source.name}</span>
                                          {source.requires_auth && (
                                            <Lock size={12} className='opacity-75' />
                                          )}
                                        </button>
                                      );
                                    })}
                                </div>
                              </div>
                            )}

                            {/* Empty state */}
                            {literatureSourcesList.length === 0 && (
                              <div className='text-center py-8 text-[#6B7280] text-sm'>
                                <Globe size={24} className='mx-auto mb-2 opacity-50' />
                                {t(
                                  'enhancedChatInput.noLiteratureSourcesAvailable',
                                  'No literature sources available'
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </form>
    </div>
  );
};

export default EnhancedChatInput;
